import React, { useEffect, useState, MouseEvent } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { FieldGuide, Label, TextInputLarge } from '../../../components/forms/input';
import { FieldValues, useForm } from 'react-hook-form';
import { useQuery } from '../../../hooks/use-query';
import { Checkbox2 } from '../../../components/forms/checkbox';
import * as Button from '../../../components/button/button';
import { Select2 } from '../../../components/forms/select';
import { LayerTree, MapLayer, LayerTypes } from '../../geo-info/types/map-layer';
import { usePutMutation } from '../../../hooks/use-mutations';
import { Color, ColorResult, SketchPicker } from 'react-color';
import { Icons } from '../../../components/icons/icons';
import { getColorFromString } from '../../../utils/style';
import { createJSONFile } from '../../../utils/json-file';

const blackColor = '#000';

export const General = (): JSX.Element => {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const configId = params.configId as string;
  const layerId = searchParams.get('selectedLayerId') || params.layerId as string;
  const { register, handleSubmit, setValue, getValues, watch } = useForm();
  const [colorPickerVisibility, setColorPickerVisibility] = useState<boolean>(false);
  const color = watch('color');
  const layerTree = useQuery<LayerTree>({ path: `/map-layers/tree/${layerId}?configurationId=${configId}`, options: { keepPreviousData: true } });
  const exportLayer = useQuery<LayerTree>({ path: `/map-layers/tree/${layerId}/export?configurationId=${configId}`, options: { enabled: false } });
  const layerPutMutation = usePutMutation<MapLayer>({ path: `/map-layers/${layerId}`, cacheKey: `/map-layers/tree/${layerId}?configurationId=${configId}` });
  const [currentColor, setCurrentColor] = useState<Color>();

  const clickHandler = (event: any): void => {
    if (event.currentTarget === document) {
      setColorPickerVisibility(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', clickHandler);
    return (): void => {
      document.removeEventListener('click', clickHandler);
    };
  }, []);

  useEffect(() => {
    if (layerTree.data?.layer) {
      setLayerFields();
    }
  }, [layerTree.data?.layer]);

  const setLayerFields = (): void => {
    for (const key in layerTree.data?.layer) {
      if (['name', 'color', 'enabled', 'sharedId', 'isHidden', 'userFolder', 'folderUserType', 'selectable', 'defaultOpen', 'isPairedSet', 'description'].includes(key)) {
        setValue(key, layerTree.data?.layer[key as keyof MapLayer]);
      }
    }
  };

  const onSubmit = (fields: FieldValues): void => {
    layerPutMutation.mutate({ ...fields, configurationId: configId });
  };

  const showColorPicker = (event: MouseEvent<HTMLDivElement>): void => {
    event.stopPropagation();
    event.nativeEvent.stopImmediatePropagation();
    setColorPickerVisibility(true);
  };

  const onChangeComplete = (color: ColorResult): void => {
    setCurrentColor(color.rgb);
    setValue('color', JSON.stringify(color.rgb));
  };

  const onChange = (color: ColorResult): void => {
    setCurrentColor(color.rgb);
  };

  const handleExport = async (): Promise<void> => {
    const result = await exportLayer.refetch();
    createJSONFile((layerTree.data?.layer.name as string || 'layer-tree'), JSON.stringify(result.data));
  };

  const isFolder = layerTree?.data?.layer?.type === LayerTypes.FOLDER;
  const isVirtualLayer = layerTree?.data?.layer?.type === LayerTypes.VIRTUAL_LAYER;

  return <form onSubmit={handleSubmit(onSubmit)}>
    <div className="mb-4">
      <Label name="name">Shared Id</Label>
      <TextInputLarge {...register('sharedId')} placeholder="Shared Id" disabled />
      <FieldGuide>Shared Id of layer/folder.</FieldGuide>
    </div>
    <div className="mb-4">
      <Label name="name">Name</Label>
      <TextInputLarge {...register('name')} placeholder="Name" />
      <FieldGuide>Name of the layer/folder.</FieldGuide>
    </div>
    {!isFolder && !isVirtualLayer && (
      <div className="mb-4">
        <Label name="description">Description</Label>
        <TextInputLarge {...register('description')} placeholder="Description" />
      </div>
    )}
    {(isFolder || isVirtualLayer) && (
      <div className="mb-4 d-flex align-items-center">
        <Label className="m-0">Color</Label>
        <div className="color-field-container pointer" onClick={showColorPicker}>
          <div className="color-field" style={{ background: color ? getColorFromString(color as string) : blackColor }} />
          <Icons.ArrowDown className="select-arrow" width="18" />
          {colorPickerVisibility && <SketchPicker color={currentColor} onChangeComplete={onChangeComplete} onChange={onChange} />}
        </div>
      </div>
    )}
    {isFolder && (
      <div className="mb-4">
        <Checkbox2 {...register('defaultOpen')} label="Open by default" defaultChecked={getValues().defaultOpen} />
      </div>
    )}
    {!isFolder && (
      <div className="mb-4">
        <Checkbox2 {...register('enabled')} label="Enabled" defaultChecked={getValues().enabled} />
      </div>
    )}
    <div className="mb-4">
      <Checkbox2 {...register('isHidden')} label="Hidden" defaultChecked={getValues().isHidden} />
    </div>
    {isFolder && (
      <div className="mb-4">
        <Checkbox2 {...register('selectable')} label="Is Selectable" defaultChecked={getValues().selectable} />
      </div>
    )}
    {isVirtualLayer && <div className="mb-4">
      <Checkbox2 {...register('isPairedSet')} label="Is Paired Set" defaultChecked={getValues().isPairedSet} />
    </div>}
    {isFolder && <div className="mb-4">
      <Checkbox2 {...register('userFolder')} label="Is user folder" defaultChecked={getValues().userFolder} />
    </div>}
    {(isFolder || isVirtualLayer) && (
      <div className="mb-4">
        <Label name="cache">User Folder Type</Label>
        <Select2 { ...register('folderUserType')}>
          <option value="">User folder type</option>
          <option value="user">User</option>
          <option value="favorites">Favorites</option>
          <option value="recent">Recent</option>
        </Select2>
      </div>
    )}
    <div className="d-flex justify-content-end">
      <Button.LightBlack onClick={handleExport} disabled={layerPutMutation.isLoading || exportLayer.isLoading}>
        Export
      </Button.LightBlack>
      <Button.Primary type="submit" className="ms-2" disabled={layerPutMutation.isLoading || exportLayer.isLoading}>
        Update
      </Button.Primary>
    </div>
  </form>;
};
